/**
 * @author Luca Filip
 * @description Constants used throughout the application for easier refactoring
 * @namespace constants
 * @example public static EXAMPLE_CONSTANT = 'My example constant!';
 */
export class AppConstants {
  public static USER = 'user';

  // target groups
  public static qualityPartner = 'targetGroup.qualityPartner';
  public static architectPlanner = 'targetGroup.architectPlanner';
  public static dealer = 'targetGroup.dealer';
  public static gardenLandscapers = 'targetGroup.gardenLandscapers';
  public static housingSociety = 'targetGroup.housingSociety';
  public static others = 'targetGroup.others';
  public static privateCustomer = 'targetGroup.privateCustomer';
  public static internalSL = 'targetGroup.internalSL';
  public static internalBranch = 'targetGroup.internalBranch';
  public static subsidiary = 'targetGroup.subsidiary';
  public static licensee = 'targetGroup.licensee';

  // filters
  public static all = 'All';
  public static active = 'active';
  public static notActive = 'not-active';
  public static confirmed = 'confirmed';
  public static unconfirmed = 'unconfirmed';
  public static yes = 'Yes';
  public static no = 'No';

  // list of all target groups (to be updated when a new TG is inserted)
  public static targetGroupsArray = [
    AppConstants.all,
    'targetGroup.qualityPartner',
    'targetGroup.architectPlanner',
    'targetGroup.dealer',
    'targetGroup.gardenLandscapers',
    'targetGroup.housingSociety',
    'targetGroup.others',
    'targetGroup.privateCustomer',
    'targetGroup.internalSL',
    'targetGroup.internalBranch',
    'targetGroup.subsidiary',
    'targetGroup.licensee',
    'targetGroup.fabricator'
  ];

  // Dictionary of all countries with countrycodes
  public static countryDict = {
    'dz': 'ALGERIA',
    'ar': 'ARGENTINA',
    'au': 'AUSTRALIA',
    'at': 'AUSTRIA',
    'be': 'BELGIUM',
    'br': 'BRAZIL',
    'cl': 'CHILE',
    'cn': 'CHINA',
    'hr': 'CROATIA',
    'cz': 'CZECH_REPUBLIC',
    'dk': 'DENMARK',
    'ee': 'ESTONIA',
    'fi': 'FINLAND',
    'fr': 'FRANCE',
    'de': 'GERMANY',
    'ge': 'GEORGIA',
    'gr': 'GREECE',
    'hu': 'HUNGARY',
    'hk': 'HONG_KONG',
    'in': 'INDIA',
    'id': 'INDONESIA',
    'ie': 'IRELAND',
    'it': 'ITALY',
    'jp': 'JAPAN',
    'kz': 'KAZAKHSTAN',
    'kg': 'KYRGYZSTAN',
    'kw': 'KUWAIT',
    'lb': 'LEBANON',
    'li': 'LIECHTENSTEIN',
    'lu': 'LUXEMBOURG',
    'mx': 'MEXICO',
    'nl': 'NETHERLANDS',
    'nz': 'NEW_ZEALAND',
    'no': 'NORWAY',
    'om': 'OMAN',
    'ph': 'PHILIPPINES',
    'pl': 'POLAND',
    'pt': 'PORTUGAL',
    'ro': 'ROMANIA',
    'ru': 'RUSSIA',
    'sa': 'SAUDI_ARABIA',
    'rs': 'SERBIA',
    'sg': 'SINGAPORE',
    'sk': 'SLOVAKIA',
    'si': 'SLOVENIA',
    'za': 'SOUTH_AFRICA',
    'kr': 'SOUTH_KOREA',
    'es': 'SPAIN',
    'lk': 'SRI_LANKA',
    'se': 'SWEDEN',
    'ch': 'SWITZERLAND',
    'tw': 'TAIWAN',
    'th': 'THAILAND',
    'tn': 'TUNISIA',
    'tr': 'TURKEY',
    'ua': 'UKRAINE',
    'ae': 'UNITED_ARAB_EMIRATES',
    'gb': 'UNITED_KINGDOM',
    'us': 'UNITED_STATES'
  };

  public static rootPath = '/apps/umng';
  public static authDetailsEndpoint = this.rootPath + '/api/authorize/details';
  public static validationEndpoint = this.rootPath + '/ui/user/path-information';
  public static checkAuthenticationStateEndpoint = this.rootPath + '/ui/authentication/state';
  public static authenticationRedirectEndpoint = this.rootPath + '/ui/authentication/redirect';
  public static forgotPasswordEndpoint = this.rootPath + '/api/password/forgot';
  public static resetPasswordEndpoint = this.rootPath + '/api/password/reset';
  public static updatePasswordEndpoint = this.rootPath + '/api/password';
  public static dataPrivacyNoticeEndpoint = this.rootPath + '/content/data-privacy-notice';
  public static confirmRegistrationEndpoint = this.rootPath + '/user/action/confirm-registration';
  public static verifyEmailEndpoint = this.rootPath + '/user/action/verify-email';

  /* alerts */
  public static alertSuccess = 'alert-success';
  public static alertDanger = 'alert-danger';
  public static alertWarning = 'alert-warning';


  /* create/edit modal names */
  public static customerByCustomerNumberModal = 'customerByCustomerNumber';
  public static targetGroupModal = 'targetGroup';
  public static rolesModal = 'roles';

  /* route paths */
  // open paths
  public static userRegistrationPath = 'user-registration';

  // authenticated paths
  public static homePage = 'home';
  public static myProfile = 'my-profile';
  public static contactPerson = 'contact-person';

  // country-manager (&admin) paths
  public static slxAdminManageUsers = 'manage-users';
  public static slxUserDetailPath = 'slx-user-detail';

  public static slxAdminManageCustomers = 'manage-customers';
  public static editCustomer = 'edit-customer';
  public static createCustomer = 'create-customer';

  // customer-manager paths
  public static changeCompanyData = 'change-company-data';
  public static adminUsersOverviewPath = 'admin-users-overview';
  public static customerUserDetailPath = 'customer-user-detail';

  public static myProfilechangePassword = 'my-profile-change-password';
  public static confirmationPage = 'confirmation';
  public static forgotPassword = 'forgot-password';
  // pages behind the links from confirmation mails
  public static confirmRegistration = 'confirm-registration';
  public static verifyEmail = 'verify-email';
  public static newsletterAgreement = 'confirm-newsletter-sub';

  // public static verifyEmailSetupPassword = 'verify-email-setup-password';
  public static resetPassword = 'reset-password';

  public static error = 'error';
  public static unmatchedPath = '**';

  /* Language translations - these should be changed according to the values from the translations files */
  public static english = 'English';
  public static german = 'German';
  public static englisch = 'Englisch';
  public static deutsche = 'Deutsche';

  /* Sorting */
  public static sortCustomerNumberField = 'CUSTOMER_NUMBER';
  public static sortCompanyField = 'CUSTOMER_NAME';
  public static sortLastNameField = 'LAST_NAME';
  public static sortFirstNameField = 'FIRST_NAME';
  public static sortCountryCodeField = 'COUNTRY_CODE';
  public static sortStatusField = 'ACTIVE';
  public static sortConfirmedField = 'CONFIRMED';
  public static sortIsCustomerManagerField = 'CUSTOMER_MANAGER';
  public static sortCreationDateField = 'CREATION_DATE';
  public static sortLastLoginField = 'LAST_LOGIN_DATE';

  public static titleCodes = ['title.mrs', 'title.mr', 'title.ms', 'title.divers', 'title.company'];
  // disabled title codes will be shown on users, but can not be selected. (e.g. 'MS' may be selected in england)
  public static disabledSalutationCodes = ['title.ms', 'title.company'];
  public static languageCodes = ['language.en', 'language.de', 'language.fr', 'language.nl'];
  public static userRoles = [
    'role.cmsUnconfirmedQualitypartner',
    'role.cmsUnconfirmedDealer',
    'role.cmsUnconfirmedGardenLandscapers',
    'role.cmsUnconfirmedArchitect',
    'role.cmsUnconfirmedHousingsociety',
    'role.cmsUnconfirmedOther',
    'role.cmsConfirmedArchitect',
    'role.cmsConfirmedDealer',
    'role.cmsConfirmedGardenLandscapers',
    'role.cmsConfirmedHousingsociety',
    'role.cmsConfirmedOther',
    'role.cmsConfirmedQualitypartner',
    'role.cmsLizenznehmer',
    'role.cmsPricelist',
    'role.cmsSlMitarbeiter',
    'role.cmsTochterMitarbeiter',
    'role.tochtergesellschaft',

    'role.darchiv',
    'role.darchivEditor',
    'role.darchivReader',
    'role.darchivSupportAccess',
    'role.darchivAssemblyFolderReader',
    'role.darchivAssemblyFolderSettings',
    'role.darchivAssemblyFolderWriter',
    'role.darchivFiBuReader',
    'role.darchivFiBuEditor',
    'role.delightVRadmin1',
    'role.delightVRuser',
    'role.gestellverwaltungAdmin',
    'role.gestellverwaltungBuchen',
    'role.haendlersucheAdmin',
    'role.haendlersucheEditor',
    'role.haendlersucheUser',
    'role.kapaRulesAdmin',
    'role.kapaRulesUser',
    'role.leads',
    'role.licmgrStaff',
    'role.liefAdmin',
    'role.liefEditor',
    'role.liefMaster',
    'role.liefOutputReader',
    'role.liefReader',
    'role.mailserviceAdmin',
    'role.mailserviceCaller',
    'role.mailserviceUser',
    'role.menuAdmin',
    'role.montageAdmin',
    'role.montageBackoffice',
    'role.montageBackofficeReader',
    'role.montageClerkHR',
    'role.montagePlanner',
    'role.montageProjectAnalyst',
    'role.montageScheduler',
    'role.montageSettings',
    'role.montageSuperuser',
    'role.montageTeamlead',
    'role.montageTerminalMonitor',
    'role.montageWorker',
    'role.montcalcAdmin',
    'role.montcalcMaintainer',
    'role.montcalcMaintainerSL',
    'role.montcalcSuperuser',
    'role.montcalcUser',
    'role.myslxSystemUser',
    'role.optabAdmin',
    'role.optabAccountingAdmin',
    'role.optabAccountingReader',
    'role.optabConstructionAdmin',
    'role.optabConstructionReader',
    'role.optabMeasurementAdmin',
    'role.optabMeasurementReader',
    'role.optabObjectReader',
    'role.optabObjectAdmin',
    'role.optabPlanningOperationAdmin',
    'role.optabPlanningOperationReader',
    'role.optabPurchasingAdmin',
    'role.optabPurchasingReader',
    'role.optabSalesReader',
    'role.optabSalesReaderDV',
    'role.optabServiceAdmin',
    'role.optabServiceReader',
    'role.optabTechnicalReviewReader',
    'role.orderStatusAdmin',
    'role.orderStatusAssemblyDocuments',
    'role.orderStatusDocumentReader',
    'role.orderStatusSalesSL',
    'role.orderStatusUser',
    'role.optabSuperuser',
    'role.pekoAdmin',
    'role.pekoEmployee',
    'role.pekoHr',
    'role.pekoManager',
    'role.pekoItPolicy',
    'role.schneelastApp',
    'role.schneelastAppInternal',
    'role.shop',
    'role.shopAdmin',
    'role.serviceAdmin',
    'role.serviceBackoffice',
    'role.serviceBackofficeAdmin',
    'role.serviceSales',
    'role.serviceSLServiceViewer',
    'role.serviceSuperuser',
    'role.serviceTechnician',
    'role.serviceSalesRepresentative',
    'role.sichtenpflege',
    'role.sichtenpflegeAdmin',
    'role.slxbsAdmin',
    'role.slxbsCanOrder',
    'role.slxbsHaendler',
    'role.slxbsLizenznehmer',
    'role.slxbsSeePrices',
    'role.slxbsStammdaten',
    'role.slxbsTochter',
    'role.slxicUser',
    'role.slxpfdevDeveloper',
    'role.sorakelAdmin',
    'role.sorakelUser',
    'role.solarluxAdmin',
    'role.techdocsInternalDocuments',
    'role.techdocsShopfloor',
    'role.techdocsSpecialSolutions',
    'role.umngAppManagerMNTG',
    'role.umngAppManagerSLXBS',
    'role.umngCountryManager',
    'role.umngCustomerManager',
    'role.umngManager',
    'role.umngSrvDataReader',
    'role.umngSrvDataWriter',

    // Authorizations from here on are deprecated or belong to archived apps. However, they are kept here temporarily.,
    'role.energielabel',
    'role.grauthoffGti',
    'role.keyAccountManager',
    'role.pekoClerk',
    'role.salesRepresentative',
    'role.uwCalculator',
    'role.uwCalculatorInternal',

  ];
  public static globalLanguageSelectorData = [
    {value: 'en', label: 'EN'},
    {value: 'fr', label: 'FR'},
    {value: 'de', label: 'DE'},
    {value: 'nl', label: 'NL'}
  ];

  public static globalCountrySelectorData = [
    {value: 'ar', label: 'AR'},
    {value: 'au', label: 'AU'},
    {value: 'at', label: 'AT'},
    {value: 'be', label: 'BE'},
    {value: 'br', label: 'BR'},
    {value: 'cl', label: 'CL'},
    {value: 'cn', label: 'CN'},
    {value: 'hr', label: 'HR'},
    {value: 'cz', label: 'CZ'},
    {value: 'dk', label: 'DK'},
    {value: 'fi', label: 'FI'},
    {value: 'fr', label: 'FR'},
    {value: 'de', label: 'DE'},
    {value: 'gr', label: 'GR'},
    {value: 'hu', label: 'HU'},
    {value: 'hk', label: 'HK'},
    {value: 'in', label: 'IN'},
    {value: 'id', label: 'ID'},
    {value: 'ie', label: 'IE'},
    {value: 'it', label: 'IT'},
    {value: 'jp', label: 'JP'},
    {value: 'li', label: 'LI'},
    {value: 'lu', label: 'LU'},
    {value: 'mx', label: 'MX'},
    {value: 'nl', label: 'NL'},
    {value: 'nz', label: 'NZ'},
    {value: 'no', label: 'NO'},
    {value: 'pl', label: 'PL'},
    {value: 'pt', label: 'PT'},
    {value: 'ro', label: 'RO'},
    {value: 'ru', label: 'RU'},
    {value: 'sa', label: 'SA'},
    {value: 'rs', label: 'RS'},
    {value: 'sg', label: 'SG'},
    {value: 'sk', label: 'SK'},
    {value: 'si', label: 'SI'},
    {value: 'za', label: 'ZA'},
    {value: 'kr', label: 'KR'},
    {value: 'es', label: 'ES'},
    {value: 'lk', label: 'LK'},
    {value: 'se', label: 'SE'},
    {value: 'ch', label: 'CH'},
    {value: 'tw', label: 'TW'},
    {value: 'th', label: 'TH'},
    {value: 'tn', label: 'TN'},
    {value: 'tr', label: 'TR'},
    {value: 'ua', label: 'UA'},
    {value: 'ae', label: 'AE'},
    {value: 'gb', label: 'GB'},
    {value: 'us', label: 'US'}

  ];

  // slx admin create-edit user
  public static CREATE_MODE = 'create';
  public static EDIT_MODE = 'edit';

  // language abreviations for translate service
  public static englishLanguageCode = 'en';
  public static germanLanguageCode = 'de';
  public static frenchLanguageCode = 'fr';
  public static dutchLanguageCode = 'nl';

  // terms and services router links
  public static englishTermsAndConditionsLink = 'https://solarlux.com/en/copyright/privacy-policy.html';
  public static germanTermsAndConditionsLink = 'https://solarlux.com/de-de/copyright/datenschutz.html';
  public static frenchTermsAndConditionsLink = 'https://solarlux.com/fr/copyright/protection-des-donnees.html';
  public static dutchTermsAndConditionsLink = 'https://solarlux.com/nl/copyright/privacy-verklaring.html';
}
